import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { View } from 'react-native';

import _ from 'lodash';
import PropTypes from 'prop-types';

import { QuickActionMenu } from '@vfgroup-oneplatform/foundation/Components';
import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';

import useKeyboardInfo from '../../Hooks/KeyboardAvoiding/KeyboardAvoiding.Hooks';
import { RenderConfirmationBody as Confirmation } from '../ConfirmationQuickAction/ConfirmationQuickAction';

import { Failure, Loading, Success } from './Components';
import styles from './ConfirmationModalJourney.style';

const ConfirmationModalJourneySteps = {
  confirm: 'confirm',
  loading: 'loading',
  success: 'success',
  failure: 'failure',
};

const ConfirmationModalJourney = forwardRef(
  ({ functionToBeExecuted, confirmationChildren }, ref) => {
    const theme = useTheme();
    const [isQAVisible, setIsQAVisible] = useState(false);
    const [confirmationStep, setConfirmationStep] = useState(ConfirmationModalJourneySteps.confirm);
    const _configs = useRef({});
    const { keyboardHeight } = useKeyboardInfo();
    const confirmationRef = useRef(null);

    useImperativeHandle(ref, () => {
      return {
        openQAM,
        setConfigs,
        setConfirmationButtonDisableState: confirmationRef?.current?.setConfirmationButtonDisable,
      };
    });
    const openQAM = () => {
      setIsQAVisible(true);
    };
    const closeQAM = () => {
      setIsQAVisible(false);
      setConfirmationStep(ConfirmationModalJourneySteps.confirm);
    };
    const onExecuteAction = async () => {
      if (_.isFunction(functionToBeExecuted)) {
        setConfirmationStep(ConfirmationModalJourneySteps.loading);
        try {
          await functionToBeExecuted(_configs.current.dataToBeSent);
          setConfirmationStep(ConfirmationModalJourneySteps.success);
        } catch (errorObj) {
          _configs.current[ConfirmationModalJourneySteps.failure] = {
            ..._configs.current[ConfirmationModalJourneySteps.failure],
            ...(!!errorObj?.message && { description: errorObj?.message }),
          };
          setConfirmationStep(ConfirmationModalJourneySteps.failure);
        }
      }
    };
    const closeQAMAndInvokeOnPress = ({ onPress = () => {} } = {}) => {
      return () => {
        closeQAM();
        onPress();
      };
    };
    const setConfigs = ({
      dataToBeSent,
      confirmationQuickActionConfig,
      failureConfigScreen,
      successConfigScreen,
      loadingScreenConfig,
    }) => {
      _configs.current.dataToBeSent = dataToBeSent;
      _configs.current[ConfirmationModalJourneySteps.confirm] = {
        withKeyboardHeight: true,
        ...confirmationQuickActionConfig,
      };
      _configs.current[ConfirmationModalJourneySteps.loading] = {
        ...loadingScreenConfig,
      };

      _configs.current[ConfirmationModalJourneySteps.success] = {
        ...successConfigScreen,
      };

      _configs.current[ConfirmationModalJourneySteps.failure] = {
        ...failureConfigScreen,
      };
    };

    const renderConfirmation = () => {
      return (
        <Confirmation
          ref={confirmationRef}
          theme={theme}
          children={confirmationChildren}
          onCancelPress={closeQAM}
          onConfirmPress={onExecuteAction}
          {..._configs.current[ConfirmationModalJourneySteps.confirm]}
        />
      );
    };
    const renderLoading = () => {
      const { subTitle } = _configs.current[ConfirmationModalJourneySteps.loading];
      return <Loading subTitle={subTitle} />;
    };
    const renderSuccess = () => {
      return (
        <Success
          {..._configs.current[ConfirmationModalJourneySteps.success]}
          closeQAMAndInvokeOnPress={closeQAMAndInvokeOnPress}
        />
      );
    };
    const renderFailure = () => {
      return (
        <Failure
          {..._configs.current[ConfirmationModalJourneySteps.failure]}
          onTryAgainPress={onExecuteAction}
          closeQAM={closeQAM}
        />
      );
    };
    const getQAMProps = () => ({
      type: 'secondary',
      isVisible: isQAVisible,
      title: _configs.current[ConfirmationModalJourneySteps.confirm]?.title,
      withCloseButton: false,
      headerStyle: styles.headerStyle,
      withKeyboardAvoid: true,
      scrollViewProps: { keyboardShouldPersistTaps: 'handled' },
    });

    const renderContentBody = {
      [ConfirmationModalJourneySteps.confirm]: renderConfirmation,
      [ConfirmationModalJourneySteps.loading]: renderLoading,
      [ConfirmationModalJourneySteps.success]: renderSuccess,
      [ConfirmationModalJourneySteps.failure]: renderFailure,
    };

    return (
      isQAVisible && (
        <QuickActionMenu {...getQAMProps()}>
          <View
            style={styles.containerStyle(
              _configs.current[ConfirmationModalJourneySteps.confirm]?.withKeyboardHeight
                ? keyboardHeight
                : 0,
            )}
          >
            {renderContentBody[confirmationStep]()}
          </View>
        </QuickActionMenu>
      )
    );
  },
);

ConfirmationModalJourney.propTypes = {
  functionToBeExecuted: PropTypes.func.isRequired,
  confirmationChildren: PropTypes.node,
};

export default ConfirmationModalJourney;
